import useConfiguration from '@/composables/configuration/configuration'
import { reactive } from 'vue'
import useAdvertPositionsManager from '@/composables/advert/advertPositionsManager'
import usePostsLoader from '@/composables/post/postLoader'
import usePostList, { PostsRoot } from '@/composables/post/postList'
import useEventBuffer from '@/composables/eventBuffer/eventBuffer'

const ADVERT_EVENT_NAME = 'anzu.forum.advertSlotReady'
//
export interface Dictionary<T> {
  [key: string]: T
}
//
export class Advert {
  slotName: string
  divId: string
  renderAfter: boolean
  propagated = false

  constructor(slotName: string, divId: string, renderAfter = true) {
    this.slotName = slotName
    this.divId = divId
    this.renderAfter = renderAfter
  }
}

const advertPositionsList: Dictionary<Advert> = reactive({})
let lastPositionIndex = 1

const {
  popNextAdvertPosition,
  pushNextAdvertPosition,
} = useAdvertPositionsManager()
const { dispatchEvent } = useEventBuffer()

let minId: number | undefined = undefined
let maxId: number | undefined = undefined
let minRootId: number | undefined = undefined
let maxRootId: number | undefined = undefined

function getMaxPos(): number | undefined {
  if (!maxId) {
    return undefined
  }
  const { findPostRootIndex } = usePostList()

  const pos = findPostRootIndex(maxId)
  if (pos >= 0) {
    return pos + 1
  }

  return undefined
}

function getMinPos(): number | undefined {
  if (!minId) {
    return undefined
  }
  const { findPostRootIndex } = usePostList()

  return findPostRootIndex(minId)
}

function isLoadingBefore(): boolean {
  const { postTree } = usePostsLoader()

  return (
    typeof minRootId !== 'undefined' &&
    postTree.length > 0 &&
    postTree[0].id !== minRootId
  )
}

function isLoadingNext(): boolean {
  const { postTree } = usePostsLoader()

  return (
    typeof maxRootId === 'undefined' ||
    (postTree.length > 0 && postTree[postTree.length - 1].id !== maxRootId)
  )
}

function initAdv(renderAfter = true): Advert {
  const { configuration } = useConfiguration()

  return new Advert(
    configuration.widget.advertSlotPrefixName + lastPositionIndex,
    configuration.widget.advertSlotPrefixId + lastPositionIndex,
    renderAfter
  )
}

let weightSum = 0
function tryToPlaceAdv(
  postRoot: PostsRoot,
  advStep: number,
  renderAfter = true
): boolean {
  weightSum += postRoot.post ? 1 : 0
  weightSum += postRoot.children.length

  if (weightSum >= advStep) {
    weightSum = 0

    advertPositionsList[postRoot.id] = initAdv(renderAfter)
    lastPositionIndex++

    if (!minId || minId > postRoot.id) {
      minId = postRoot.id
    }
    if (!maxId || maxId < postRoot.id) {
      maxId = postRoot.id
    }

    return true
  }

  return false
}

// todo reset function! (change hash)

export default function useAdvertManager() {
  const { configuration } = useConfiguration()

  const initAdverts = () => {
    if (configuration.widget.advertDisabled) {
      return
    }

    const { postTree } = usePostsLoader()

    let advStep = popNextAdvertPosition()
    if (typeof advStep === 'undefined') {
      return
    }

    if (postTree.length === 0) {
      return
    }

    weightSum = 0
    if (isLoadingBefore()) {
      for (let i = getMinPos() ?? 0; i > 0; i--) {
        if (tryToPlaceAdv(postTree[i] as PostsRoot, advStep, false)) {
          advStep = popNextAdvertPosition()
          if (typeof advStep === 'undefined') {
            break
          }
        }
      }
    } else if (isLoadingNext()) {
      for (let i = getMaxPos() ?? 0; i < postTree.length; i++) {
        if (tryToPlaceAdv(postTree[i] as PostsRoot, advStep)) {
          advStep = popNextAdvertPosition()
          if (typeof advStep === 'undefined') {
            break
          }
        }
      }
    }

    if (typeof advStep !== 'undefined') {
      pushNextAdvertPosition(advStep)
    }

    minRootId = postTree[0].id
    maxRootId = postTree[postTree.length - 1].id
  }
  const propagateAdv = (advert: Advert) => {
    dispatchEvent(ADVERT_EVENT_NAME, {
      slotName: advert.slotName,
      divId: advert.divId,
    })
  }

  return {
    advertPositionsList,
    initAdverts,
    propagateAdv,
  }
}
